import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useIsMobile from "../../../hooks/useIsMobile";
import { formatMeasurementAverageRangeDates } from "../../../utils/chartUtils";
import { formatLatestBloodPressure } from "../../../utils/dateFormatter";
import { getLatestBloodPressureValue } from "../../../utils/measurementsUtils";
import { CustomChartLegend } from "../../CustomChartLegend/CustomChartLegend";
import { TimeInRangeSummary } from "../TimeInRangeSummary/TimeInRangeSummary";
import {
  ChartLegendContainer,
  Container,
  DateLabel,
  Divider,
  MainValueContainer,
  MainValueContainerFirst,
  MainValuesContainer,
  NumberLabel,
  TextLabel,
} from "./CustomModalChartLegend.style";

export function CustomModalChartLegend(props: any) {
  const { t } = useTranslation();
  const {
    mainValues,
    diastolicMainValues,
    empty,
    baseline,
    trendLine,
    isBloodPressure,
    chartConfig,
  } = props;
  const { min, max, average } = mainValues;

  const { isMobile } = useIsMobile();
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setDisabled(!min && !max && !average);
  }, [mainValues]);

  const getAverageValue = () => {
    if (!isBloodPressure && average) return average;

    if (isBloodPressure) {
      return `${mainValues.average != null ? mainValues.average : "—"}/${
        diastolicMainValues.average != null ? diastolicMainValues.average : "—"
      }`;
    }

    return "—";
  };

  const chartLegendContainer = (
    <ChartLegendContainer extraPadding={empty}>
      <CustomChartLegend
        {...props}
        focusMode
        baseline={baseline}
        trendLine={trendLine}
        chartConfig={chartConfig}
      />
    </ChartLegendContainer>
  );

  const notAvailableText = t("chart_legend_notAvailable");

  const renderDate = (validDateStr: string | undefined) => {
    if (disabled) return notAvailableText;
    return validDateStr ?? "-";
  };

  // EN-5232: continuous glucose replaces highest/lowest/mean with time in range.
  const mainValuesContainer = mainValues.timeInRange ? (
    <TimeInRangeSummary
      timeInRange={mainValues.timeInRange}
      dateRange={formatMeasurementAverageRangeDates(
        mainValues.minUnarchivedDate,
        mainValues.maxUnarchivedDate
      )}
    />
  ) : (
    <MainValuesContainer>
      {!isBloodPressure && (
        <>
          <MainValueContainerFirst>
            <NumberLabel disabled={disabled}>{max || "—"}</NumberLabel>
            <TextLabel disabled={disabled}>
              {t("chart_legend_highest")}
            </TextLabel>
            <DateLabel disabled={disabled}>
              {renderDate(
                dayjs(mainValues.maxValueDate).isValid()
                  ? dayjs(mainValues.maxValueDate).format("MMM DD, YYYY")
                  : undefined
              )}
            </DateLabel>
          </MainValueContainerFirst>

          <MainValueContainer>
            <NumberLabel disabled={disabled}>{min || "—"}</NumberLabel>
            <TextLabel disabled={disabled}>
              {t("chart_legend_lowest")}
            </TextLabel>
            <DateLabel disabled={disabled}>
              {renderDate(
                dayjs(mainValues.minValueDate).isValid()
                  ? dayjs(mainValues.minValueDate).format("MMM DD, YYYY")
                  : undefined
              )}
            </DateLabel>
          </MainValueContainer>
        </>
      )}
      {isBloodPressure && (
        <MainValueContainer>
          <NumberLabel disabled={disabled}>
            {getLatestBloodPressureValue(mainValues, diastolicMainValues)}
          </NumberLabel>
          <TextLabel disabled={disabled}>{t("chart_legend_latest")}</TextLabel>
          <DateLabel disabled={disabled}>
            {disabled
              ? notAvailableText
              : formatLatestBloodPressure(mainValues, diastolicMainValues)}
          </DateLabel>
        </MainValueContainer>
      )}
      <MainValueContainer>
        <NumberLabel disabled={disabled}>{getAverageValue()}</NumberLabel>
        <TextLabel disabled={disabled}>{t("chart_legend_mean")}</TextLabel>
        <DateLabel disabled={disabled}>
          {disabled
            ? notAvailableText
            : formatMeasurementAverageRangeDates(
                mainValues.minUnarchivedDate,
                mainValues.maxUnarchivedDate
              )}
        </DateLabel>
      </MainValueContainer>
    </MainValuesContainer>
  );

  return (
    <div
      style={{ bottom: !isMobile ? "-16px" : undefined, position: "relative" }}
    >
      {isMobile ? (
        <Container>
          {chartLegendContainer}
          {!empty && <Divider />}
          {mainValuesContainer}
        </Container>
      ) : (
        <Container>
          {!empty && (
            <>
              {mainValuesContainer}
              <Divider />
            </>
          )}

          {chartLegendContainer}
        </Container>
      )}
    </div>
  );
}
